import { RectangleHorizontal, RectangleVertical } from 'lucide-react';
import { DEPRECATED_IMAGE_MODEL_REPLACEMENTS } from '@inference.engine/widgets/web';
import {
  ImageModel,
  aspectRatioModelsMap,
  IMAGE_MODEL_ASPECT_RATIOS,
  IMAGE_MODEL_QUALITIES,
  IMAGE_MODEL_RESOLUTIONS,
  ImageOptions,
  referenceAccuracyModelsMap,
  REFERENCE_ACCURACY_OPTIONS,
  AMOUNT_OF_VARIATIONS_OPTIONS,
  customModelIdModelsMap,
  SUPPORTED_IMAGE_MODELS,
} from './ImageGenerationOptions.types';

export function AspectRatioIcon({ orientation }: { orientation: 'landscape' | 'portrait' }) {
  if (orientation === 'landscape') return <RectangleHorizontal className="h-3.5 w-3.5" />;
  return <RectangleVertical className="h-3.5 w-3.5" />;
}

/** A model's sizes or aspect ratios; none for a model this build does not know, as a newer server can name. */
export const aspectRatiosOf = (model: string) =>
  (IMAGE_MODEL_ASPECT_RATIOS as Partial<typeof IMAGE_MODEL_ASPECT_RATIOS>)[
    model as ImageModel | 'custom-workflow'
  ] ?? [];

function getDefaultSizeOrAspectRatio(model: ImageModel | 'custom-workflow') {
  const aspectRatios = aspectRatiosOf(model);
  if (aspectRatios.length === 0) {
    return undefined;
  }
  return aspectRatios[0].value;
}

/**
 * A chat keeps the model the user last picked, so a retired one outlives its removal from the
 * dropdown. Mapping it to the replacement here covers every entry point — both popups rebuild
 * their options through `getOptionsForModel` — and keeps the model tables from being indexed
 * by a key they no longer carry.
 */
export function resolveStoredModel(
  model: ImageModel | 'custom-workflow',
): ImageModel | 'custom-workflow' {
  return (DEPRECATED_IMAGE_MODEL_REPLACEMENTS[model] as ImageModel | undefined) ?? model;
}

/**
 * Creates ImageOptions for a given model with optional overrides.
 * Applies model-specific conditional logic to determine which optional fields
 * (aspectRatio, size, referenceAccuracy) should be set based on the model type.
 * Single source of truth for all conditional logic — shared between the V1
 * legacy popup and the V2 popup.
 */
export function getOptionsForModel(
  requestedModel: ImageModel | 'custom-workflow',
  overrides?: Partial<ImageOptions>,
): ImageOptions {
  const model = resolveStoredModel(requestedModel);
  const isCustomWorkflow = model === 'custom-workflow';
  const isAspectRatioModel = !isCustomWorkflow && aspectRatioModelsMap.includes(model);
  const isReferenceAccuracyModel = !isCustomWorkflow && referenceAccuracyModelsMap.includes(model);
  const isCustomModelIdModel = !isCustomWorkflow && customModelIdModelsMap.includes(model);
  const defaultSizeOrAspectRatio = getDefaultSizeOrAspectRatio(model);
  const supportedResolutions = !isCustomWorkflow ? IMAGE_MODEL_RESOLUTIONS[model] : undefined;
  const priorResolution = overrides?.settings?.resolution;
  const resolution =
    supportedResolutions && priorResolution
      ? supportedResolutions.includes(priorResolution)
        ? priorResolution
        : supportedResolutions[supportedResolutions.length - 1]
      : undefined;
  const supportedQualities = !isCustomWorkflow ? IMAGE_MODEL_QUALITIES[model] : undefined;
  const priorQuality = overrides?.settings?.quality ?? 'auto';
  const quality = supportedQualities
    ? supportedQualities.includes(priorQuality)
      ? priorQuality
      : supportedQualities[supportedQualities.length - 1]
    : undefined;

  return {
    settings: {
      model,
      aspectRatio: isAspectRatioModel
        ? (overrides?.settings?.aspectRatio ?? defaultSizeOrAspectRatio)
        : undefined,
      resolution,
      size: !isAspectRatioModel
        ? (overrides?.settings?.size ?? defaultSizeOrAspectRatio)
        : undefined,
      referenceAccuracy: isReferenceAccuracyModel
        ? (overrides?.settings?.referenceAccuracy ??
          REFERENCE_ACCURACY_OPTIONS[REFERENCE_ACCURACY_OPTIONS.length - 1].value)
        : undefined,
      quality,
      customModelId: isCustomModelIdModel ? (overrides?.settings?.customModelId ?? '') : undefined,
      customWorkflowId: isCustomWorkflow
        ? (overrides?.settings?.customWorkflowId ?? '')
        : undefined,
      customWorkflowInputs: isCustomWorkflow
        ? (overrides?.settings?.customWorkflowInputs ?? {})
        : undefined,
    },
    variations: overrides?.variations ?? AMOUNT_OF_VARIATIONS_OPTIONS[0].value,
    mask: overrides?.mask,
  };
}

/**
 * The options after a switch to another image model, keeping what the person set wherever the new
 * model takes it, as the Image agent's own picker keeps the resolution.
 */
export function optionsForImageModel(
  current: ImageOptions,
  requestedModel: ImageModel,
): ImageOptions {
  const takes = aspectRatiosOf(resolveStoredModel(requestedModel)).map((option) => option.value);
  const kept = (value: string | undefined) =>
    value !== undefined && takes.includes(value) ? value : undefined;
  return getOptionsForModel(requestedModel, {
    settings: {
      ...current.settings,
      model: requestedModel,
      aspectRatio: kept(current.settings.aspectRatio),
      size: kept(current.settings.size),
    },
    variations: current.variations,
    mask: current.mask,
  });
}

const sameDefined = (a: Record<string, unknown>, b: Record<string, unknown>) =>
  Object.keys({ ...a, ...b }).every((key) => a[key] === b[key]);

/** Whether the composer's image options are still exactly the defaults it started on. */
export function isImageSeed(options: Record<string, unknown>, seed: ImageOptions): boolean {
  const settings = (options.settings ?? {}) as Record<string, unknown>;
  return (
    options.mask === undefined &&
    options.variations === seed.variations &&
    sameDefined(settings, seed.settings as Record<string, unknown>)
  );
}

/** Whether this build has the settings tables for an image model the API names. */
export const isKnownImageModel = (model: unknown): model is ImageModel =>
  SUPPORTED_IMAGE_MODELS.some((option) => option.value === model);
